import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import GlobalTable from "../components/GlobalTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";

// ── Detail / accept panel ────────────────────────────────────────────────────
const IncomingViewer = ({ prescription, onClose, onAccepted }) => {
    const [accepting, setAccepting] = useState(false);
    const navigate = useNavigate();

    const accept = async () => {
        setAccepting(true);
        try {
            const res = await axiosInstance.post(`/api/clinic-prescriptions/${prescription.id}/accept/`);
            handleApiSuccess("Prescription accepted");
            onAccepted(prescription.id);
            onClose();
            // Quantity was never set by the doctor (see accept_clinic_prescription's
            // docstring) — hand off into the same dialog/flow the customer-portal
            // refill request uses to have the pharmacist set it before converting.
            navigate("/prescriptions", {
                state: {
                    prescriptionId: res.data.prescription_id,
                    clinicPrescriptionMode: true,
                    clinicSourceName: res.data.clinic_name,
                },
            });
        } catch (err) {
            handleApiError(err);
        } finally {
            setAccepting(false);
        }
    };

    return (
        <Dialog
            header={prescription ? `${prescription.patient_name} — ${prescription.clinic_name}` : ""}
            visible={!!prescription}
            style={{ width: "560px", maxWidth: "95vw" }}
            onHide={onClose}
        >
            {prescription && (
                <>
                    <div style={{ fontSize: 12.5, color: "var(--text-color-secondary)", marginBottom: 14 }}>
                        {prescription.doctor_name}
                    </div>

                    {prescription.notes && (
                        <div style={{
                            background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
                            padding: "10px 14px", fontSize: 13, color: "#1d4ed8", marginBottom: 16,
                        }}>
                            <b>Note from clinic:</b> {prescription.notes}
                        </div>
                    )}

                    {(prescription.prescribed_duration_days || prescription.dispensing_duration_days || prescription.follow_up_days) && (
                        <div style={{
                            display: "flex", gap: 16, flexWrap: "wrap",
                            background: "var(--surface-50)", border: "1px solid var(--surface-border)", borderRadius: 8,
                            padding: "10px 14px", fontSize: 12.5, color: "var(--text-color-secondary)", marginBottom: 16,
                        }}>
                            {prescription.prescribed_duration_days && (
                                <span><b>Course:</b> {prescription.prescribed_duration_days} days</span>
                            )}
                            {prescription.dispensing_duration_days && (
                                <span><b>Suggested dispense:</b> {prescription.dispensing_duration_days} days</span>
                            )}
                            {prescription.follow_up_days && (
                                <span><b>Follow-up:</b> {prescription.follow_up_days} days</span>
                            )}
                        </div>
                    )}

                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-color-secondary)", textTransform: "uppercase", marginBottom: 8 }}>
                        Items ({prescription.items.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                        {prescription.items.map((item) => (
                            <div key={item.id} style={{
                                border: "1px solid var(--surface-border)", borderRadius: 10, padding: "10px 12px",
                            }}>
                                <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-color)" }}>{item.product_name}</div>
                                <div style={{ fontSize: 12, color: "var(--text-color-secondary)", marginTop: 2 }}>
                                    {[item.dosage, item.frequency, item.quantity ? `qty ${item.quantity}` : null, item.instructions]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </div>
                                {(item.resolved_prescribed_duration || item.dispensing_duration_days) && (
                                    <div style={{ fontSize: 11.5, color: "var(--text-color-secondary)", marginTop: 3 }}>
                                        {item.resolved_prescribed_duration ? `Course ${item.resolved_prescribed_duration}d` : null}
                                        {item.resolved_prescribed_duration && item.dispensing_duration_days ? " · " : null}
                                        {item.dispensing_duration_days ? `Will dispense ${item.dispensing_duration_days}d` : null}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <Button
                        label={accepting ? "Accepting…" : "Accept prescription"}
                        icon="pi pi-check"
                        className="p-button-success"
                        style={{ width: "100%" }}
                        disabled={accepting}
                        onClick={accept}
                    />
                </>
            )}
        </Dialog>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const IncomingClinicPrescriptions = () => {
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => { fetchIncoming(); }, []);

    // Arriving from a "New prescription from <clinic>" notification (see
    // clinic.views_clinic.create_clinic_prescription's entity_route,
    // ?id=<prescription id>) — open straight to that specific record
    // instead of leaving the pharmacist to find it in the list. Same
    // ?id= convention RefillRequests.js already uses.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const targetId = params.get("id");
        if (!targetId || prescriptions.length === 0) return;
        const match = prescriptions.find((p) => p.id === targetId);
        if (match) {
            setViewing(match);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, prescriptions]);

    const fetchIncoming = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/api/clinic-prescriptions/incoming/");
            setPrescriptions(res.data);
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    // Re-fetches rather than filtering the accepted row out of local state —
    // a successful accept should still show up here (now "Accepted", linked
    // to the real Prescription) rather than disappear.
    const handleAccepted = () => {
        fetchIncoming();
    };

    const itemsTemplate = (row) => (
        <span style={{ fontSize: 12.5, color: "var(--text-color-secondary)" }}>
            {row.items.map((i) => i.product_name).join(", ")}
        </span>
    );

    const STATUS_TAG = {
        sent: { value: "Awaiting Accept", severity: "warning" },
        accepted: { value: "Accepted", severity: "success" },
        cancelled: { value: "Cancelled", severity: "danger" },
        expired: { value: "Expired", severity: "danger" },
    };
    const statusTemplate = (row) => {
        const tag = STATUS_TAG[row.status] || { value: row.status, severity: "info" };
        return <Tag value={tag.value} severity={tag.severity} />;
    };

    const actionTemplate = (row) => {
        if (row.status === "sent") {
            return (
                <Button
                    icon="pi pi-eye"
                    label="Review & Accept"
                    className="p-button-sm p-button-outlined"
                    onClick={() => setViewing(row)}
                />
            );
        }
        if (row.status === "accepted" && row.linked_prescription_id) {
            return (
                <Button
                    icon="pi pi-arrow-right"
                    label="View Prescription"
                    className="p-button-sm p-button-text"
                    onClick={() => navigate("/prescriptions", {
                        state: {
                            prescriptionId: row.linked_prescription_id,
                            clinicPrescriptionMode: true,
                            clinicSourceName: row.clinic_name,
                        },
                    })}
                />
            );
        }
        return null;
    };

    const columns = [
        { accessor: "patient_name", Header: "Patient" },
        { accessor: "clinic_name", Header: "Clinic" },
        { accessor: "doctor_name", Header: "Doctor" },
        { accessor: "items", Header: "Items", bodyTemplate: itemsTemplate },
        { accessor: "issued_at", Header: "Issued", dataType: "date" },
        { accessor: "status", Header: "Status", bodyTemplate: statusTemplate },
        { accessor: "id", Header: "Actions", bodyTemplate: actionTemplate },
    ];

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    data={prescriptions}
                    columns={columns}
                    tabletitle="Incoming from Clinics"
                    loading={loading}
                    showEditColumn={false}
                />

                <IncomingViewer
                    prescription={viewing}
                    onClose={() => setViewing(null)}
                    onAccepted={handleAccepted}
                />
            </div>
        </div>
    );
};

export default IncomingClinicPrescriptions;
